const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:8000";

export function redirectToLogin() {
  if (typeof window === "undefined") return;

  const returnTo = encodeURIComponent(window.location.href);
  window.location.assign(`${BASE}/.auth/login/aad?post_login_redirect_uri=${returnTo}`);
}

export async function ensureAuthenticated() {
  const response = await fetch(`${BASE}/.auth/me`, {
    credentials: "include",
  });

  if (response.status === 401) {
    redirectToLogin();
    return false;
  }

  // Container Apps forwards /.auth/me to FastAPI after Easy Auth admits the
  // request, so an authenticated request can receive the application's 404.
  if (response.status === 404) {
    return true;
  }

  if (!response.ok) {
    throw new Error(`Authentication check → ${response.status}`);
  }

  const identities = await response.json();
  if (!Array.isArray(identities) || identities.length === 0) {
    redirectToLogin();
    return false;
  }

  return true;
}

async function parseResponse(r, label, { redirectOn401 = true } = {}) {
  if (r.status === 401 && redirectOn401) {
    redirectToLogin();
    throw new Error(`${label} → 401`);
  }
  if (!r.ok) {
    let detail = "";
    try {
      const d = await r.json();
      if (d.detail) {
        detail = Array.isArray(d.detail)
          ? " — " + d.detail.map(e => `${e.loc?.slice(-1)[0]}: ${e.msg}`).join(", ")
          : " — " + d.detail;
      }
    } catch (_) {}
    throw new Error(`${label}${detail} → ${r.status}`);
  }
  return r.json();
}

async function request(method, path, body, opts) {
  const r = await fetch(`${BASE}${path}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });
  return parseResponse(r, `${method} ${path}`, opts);
}

const get   = (path, opts)  => request("GET",   path, undefined, opts);
const post  = (path, body)  => request("POST",  path, body);
const patch = (path, body)  => request("PATCH", path, body);

// ── Events ────────────────────────────────────────────────────────────────────
export const getEvents         = ()       => get("/events");
export const getEvent          = (id)     => get(`/events/${id}`);
export const getEventReviewers = (id)     => get(`/events/${id}/reviewers`);

// ── Shows ─────────────────────────────────────────────────────────────────────
export const getShows       = (params = {}) => get(`/shows?${new URLSearchParams(params)}`);
export const getShow        = (id)          => get(`/shows/${id}`);
export const addShow        = (body)        => post("/shows", body);
export const patchShow      = (id, body)    => patch(`/shows/${id}`, body);

// ── Companies ─────────────────────────────────────────────────────────────────
export const getCompanies      = (params = {}) => get(`/companies?${new URLSearchParams(params)}`);
export const getEventCategories = (name)       => get(`/events/categories?event=${encodeURIComponent(name)}`);
export const getCompanyFacets  = (params = {}) => get(`/companies/facets?${new URLSearchParams(params)}`);

/**
 * Every row an endpoint has for these filters, fetched a page at a time.
 *
 * For the cases that genuinely need the whole set — an export, or a review queue
 * where the user works through all of it and acts on many rows at once. A single
 * request with a big `limit` is not the same thing: it silently returns the first
 * N and the caller cannot tell that anything is missing.
 *
 * Handles both response shapes in this API: `{items, total}` and a bare array,
 * where a short page is the only signal that the end has been reached.
 */
export async function getAllPages(fetcher, params = {}, pageSize = 500, cap = 20000) {
  const out = [];
  for (let offset = 0; offset < cap; offset += pageSize) {
    const data = await fetcher({ ...params, limit: pageSize, offset });
    const items = Array.isArray(data) ? data : (data.items ?? []);
    out.push(...items);
    if (items.length < pageSize) return out;                       // bare array: short page = done
    if (!Array.isArray(data) && out.length >= (data.total ?? 0)) return out;
  }
  console.warn(`getAllPages stopped at the ${cap}-row cap — the result may be incomplete`);
  return out;
}

export const getAllCompanies = (params = {}, pageSize = 500) =>
  getAllPages(getCompanies, params, pageSize);
export const getCompany        = (id)          => get(`/companies/${id}`);
export const getContacts       = (id, showId)  => get(`/companies/${id}/contacts${showId ? `?show_id=${encodeURIComponent(showId)}` : ""}`);
export const getContactsBatch  = (company_ids, show_id) => post("/companies/contacts/batch", { company_ids, show_id: show_id ?? null });
export const patchCompany      = (id, body)    => patch(`/companies/${id}`, body);
export const patchContacts     = (id, body)    => patch(`/companies/${id}/contacts`, body);

// ── Sales Assignments ─────────────────────────────────────────────────────────
export const getSalesAssignments    = (params = {}) => get(`/assignments/sales?${new URLSearchParams(params)}`);
export const createSalesAssignment  = (body)        => post("/assignments/sales", body);
export const patchSalesAssignment   = (show_id, body) => patch(`/assignments/sales/${show_id}`, body);

// ── Marketing Assignments ─────────────────────────────────────────────────────
export const getMarketingAssignments   = (params = {}) => get(`/assignments/marketing?${new URLSearchParams(params)}`);
export const createMarketingAssignment = (body)        => post("/assignments/marketing", body);

// ── Sales Reviews ─────────────────────────────────────────────────────────────
export const getSalesReviews   = (params = {}) => get(`/reviews/sales?${new URLSearchParams(params)}`);
export const upsertSalesReview = (body)        => patch("/reviews/sales", body);
export const bulkSalesReview   = (body)        => patch("/reviews/sales/bulk", body);

// ── Marketing Reviews ─────────────────────────────────────────────────────────
export const getMarketingReviews   = (params = {}) => get(`/reviews/marketing?${new URLSearchParams(params)}`);
export const upsertMarketingReview = (body)        => patch("/reviews/marketing", body);
export const bulkMarketingReview   = (body)        => patch("/reviews/marketing/bulk", body);

// ── Pipeline ──────────────────────────────────────────────────────────────────
export const getPipeline                  = (params = {})          => get(`/pipeline?${new URLSearchParams(params)}`);
export const getPipelineDetail            = (eventId, params = {}) => get(`/pipeline/${eventId}?${new URLSearchParams(params)}`);
export const getCrossEventLeads           = (eventId, params = {}) => get(`/pipeline/${eventId}/cross-event-leads?${new URLSearchParams(params)}`);
export const getCrossEventLeadsForManager = (params = {})          => get(`/pipeline/cross-event-leads?${new URLSearchParams(params)}`);
// ── Insights ──────────────────────────────────────────────────────────────────
export const getHeatmap          = (params = {}) => get(`/insights/heatmap?${new URLSearchParams(params)}`);
export const getGrowthData       = (params = {}) => get(`/insights/growth?${new URLSearchParams(params)}`);
export const getYieldByQuarter   = (params = {}) => get(`/insights/yield-by-quarter?${new URLSearchParams(params)}`);
export const getGaps             = (params = {}) => get(`/insights/gaps?${new URLSearchParams(params)}`);
export const getTopCountries     = (params = {}) => get(`/insights/top-countries?${new URLSearchParams(params)}`);
export const getTopCategories       = (params = {}) => get(`/insights/top-categories?${new URLSearchParams(params)}`);
export const getEventsWithData      = ()             => get("/insights/events-with-data");
export const getGeopolitical        = (params = {}) => get(`/insights/geopolitical?${new URLSearchParams(params)}`);
export const getCrossEventSummary   = (params = {}) => get(`/insights/cross-event-summary?${new URLSearchParams(params)}`);

// ── Data Quality ──────────────────────────────────────────────────────────────
export const getQualityIssues        = (params = {}) => get(`/quality?${new URLSearchParams(params)}`);
export const getQualityFacets        = (params = {}) => get(`/quality/facets?${new URLSearchParams(params)}`);
export const getQualityIssueSummary  = (params = {}) => get(`/quality/summary?${new URLSearchParams(params)}`);
export const updateQualityIssue      = (id, body)    => patch(`/quality/${id}`, body);

// ── CRM ───────────────────────────────────────────────────────────────────────
export const sendToCrm = (body) => post("/crm", body);

// ── Exports ───────────────────────────────────────────────────────────────────
export const getExports    = ()     => get("/exports");
export const createExport  = (body) => post("/exports", body);

// ── Users ─────────────────────────────────────────────────────────────────────
export const getUsers = (params = {}) => get(`/users?${new URLSearchParams(params)}`);
// Identity resolution — a 401 here means "no session/identity", handled as an
// access-denied state, NOT a login redirect (which 404s on local dev).
export const getMe    = ()            => get("/users/me", { redirectOn401: false });

// ── AI Feedback ───────────────────────────────────────────────────────────────
export const getFeedback    = (params = {}) => get(`/feedback?${new URLSearchParams(params)}`);
export const upsertFeedback = (body)        => patch("/feedback", body);

// ── AI ────────────────────────────────────────────────────────────────────────
export const generateShowInsights = (body) => post("/ai/show-insights", body);
export const generateDashboardFocus = (body) => post("/ai/dashboard-focus", body);
export const generateMarketRead     = (body) => post("/ai/market-read", body);
export const generateGeopoliticalRead = (body) => post("/ai/geopolitical-read", body);

// ── Uploads ───────────────────────────────────────────────────────────────────
export const uploadExcel = (file, ite_event_id, show_id, scheduled_by = "admin", schedule = "tonight") => {
  const form = new FormData();
  form.append("file", file);
  form.append("ite_event_id", ite_event_id);
  form.append("show_id", show_id);
  form.append("scheduled_by", scheduled_by);
  form.append("schedule", schedule);
  return fetch(`${BASE}/uploads`, { method: "POST", body: form, credentials: "include" })
    .then(r => parseResponse(r, "Upload"));
};
